/**
 * Tries an Ultrahuman custom-scheme URL, then the given store URL if the app
 * does not take over. `deeplink` defaults to the bare scheme; pass a path to
 * open a specific screen. Same handoff Pulsomics uses: the page going hidden
 * cancels the store redirect. Desktop returns false so the caller can show
 * a QR instead.
 *
 * Desktop QR codes and copy-links point at GET /api/app on the current
 * origin (preview, staging, or production) so a scan tests that deploy.
 */
type Platform = 'ios' | 'android' | 'other';

const APP_SCHEME = 'ultrahuman://';
const APP_LAUNCH_GRACE_MS = 1200;

export const APPSTORE_URL =
  'https://apps.apple.com/us/app/ultrahuman-meditation-sleep/id1491286709';
export const PLAYSTORE_URL =
  'https://play.google.com/store/apps/details?id=com.ultrahuman.android';

const appHandoffPath = (store: 'ios' | 'android'): string =>
  `/api/app/?store=${store}`;

export const appHandoffUrlFromOrigin = (
  origin: string,
  store: string | null,
): string =>
  `${origin}${appHandoffPath(store === 'android' ? 'android' : 'ios')}`;

export const appHandoffUrl = (store: 'ios' | 'android'): string =>
  appHandoffUrlFromOrigin(window.location.origin, store);

const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const iPadOS =
    /Macintosh/.test(ua) &&
    'maxTouchPoints' in navigator &&
    navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

export const storeUrlForQuery = (store: string | null): string => {
  if (store === 'android') return PLAYSTORE_URL;
  return APPSTORE_URL;
};

/** Store listing for this phone, or null on desktop (caller shows a QR). */
export const storeUrlForDevice = (): string | null => {
  const platform = detectPlatform();
  if (platform === 'ios') return APPSTORE_URL;
  if (platform === 'android') return PLAYSTORE_URL;
  return null;
};

export const tryOpenUltrahumanApp = (
  storeUrl: string,
  deeplink: string = APP_SCHEME,
): boolean => {
  if (detectPlatform() === 'other') return false;

  let settled = false;
  const cancel = () => {
    settled = true;
  };

  document.addEventListener('visibilitychange', cancel, { once: true });
  window.addEventListener('pagehide', cancel, { once: true });
  window.addEventListener('blur', cancel, { once: true });

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', cancel);
    window.removeEventListener('pagehide', cancel);
    window.removeEventListener('blur', cancel);
    if (settled || document.hidden) return;
    window.location.href = storeUrl;
  }, APP_LAUNCH_GRACE_MS);

  window.location.href = deeplink;
  return true;
};
